import { useState } from "react";
import { z } from "zod";

export type ValidationState = "idle" | "valid" | "invalid";

export interface FieldValidation {
  state: ValidationState;
  message?: string;
}

interface UseFieldValidationOptions {
  value: string;
  schema?: z.ZodSchema<string>;
  /** Required field — empty string => invalid (only after touched). */
  required?: boolean;
  requiredMessage?: string;
  /** Skip validation entirely. */
  disabled?: boolean;
}

/**
 * Lightweight Zod-backed field validation.
 *
 * State is "idle" until the field has been touched (blurred or `markTouched()` called).
 * Validation reads the live value — no debouncing — so submit-time checks reflect
 * exactly what the user typed.
 */
export function useFieldValidation({
  value,
  schema,
  required,
  requiredMessage = "Ce champ est requis",
  disabled,
}: UseFieldValidationOptions) {
  const [touched, setTouched] = useState(false);

  let state: ValidationState = "idle";
  let message: string | undefined;

  if (!disabled && touched) {
    const v = value.trim();
    if (required && v.length === 0) {
      state = "invalid";
      message = requiredMessage;
    } else if (schema) {
      const result = schema.safeParse(value);
      if (result.success) {
        state = v.length > 0 ? "valid" : "idle";
      } else {
        state = "invalid";
        message = result.error.issues[0]?.message;
      }
    } else if (v.length > 0) {
      state = "valid";
    }
  }

  return {
    state,
    message,
    touched,
    onBlur: () => setTouched(true),
    markTouched: () => setTouched(true),
    reset: () => setTouched(false),
  } satisfies FieldValidation & {
    touched: boolean;
    onBlur: () => void;
    markTouched: () => void;
    reset: () => void;
  };
}
